import React, { useState, useEffect } from 'react';
import { treatmentAPI } from '../api/axios';
import Button from '../components/Button';
import { formatCurrency } from '../utils/dateUtils';
import './TreatmentManagementPage.css';

const TreatmentManagementPage = () => {
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTreatment, setEditingTreatment] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    label: '',
    category: '',
    regularPrice: '',
    eventPrice: '',
    useEventPrice: false,
    duration: '',
    displayOrder: 0,
    isActive: true,
  });

  const [formErrors, setFormErrors] = useState({});

  // 시술 목록 불러오기
  useEffect(() => {
    fetchTreatments();
  }, []);

  const fetchTreatments = async () => {
    try {
      setLoading(true);
      const response = await treatmentAPI.getAll();
      setTreatments(response.data);
      setError(null);
    } catch (err) {
      setError('시술 목록을 불러오는데 실패했습니다.');
      console.error('Error fetching treatments:', err);
    } finally {
      setLoading(false);
    }
  };

  // 모달 열기 (신규 등록)
  const handleOpenModal = () => {
    setEditingTreatment(null);
    setFormData({
      name: '',
      label: '',
      category: '',
      regularPrice: '',
      eventPrice: '',
      useEventPrice: false,
      duration: '',
      displayOrder: treatments.length,
      isActive: true,
    });
    setFormErrors({});
    setShowModal(true);
  };

  // 모달 열기 (수정)
  const handleEditTreatment = (treatment) => {
    setEditingTreatment(treatment);
    setFormData({
      name: treatment.name || '',
      label: treatment.label || '',
      category: treatment.category || '',
      regularPrice: treatment.regularPrice || '',
      eventPrice: treatment.eventPrice || '',
      useEventPrice: treatment.useEventPrice || false,
      duration: treatment.duration || '',
      displayOrder: treatment.displayOrder || 0,
      isActive: treatment.isActive !== undefined ? treatment.isActive : true,
    });
    setFormErrors({});
    setShowModal(true);
  };

  // 모달 닫기
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTreatment(null);
    setFormData({
      name: '',
      label: '',
      category: '',
      regularPrice: '',
      eventPrice: '',
      useEventPrice: false,
      duration: '',
      displayOrder: 0,
      isActive: true,
    });
    setFormErrors({});
  };

  // 입력 변경 핸들러
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // 에러 메시지 제거
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // 유효성 검사
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = '시술명은 필수입니다';
    if (!formData.label.trim()) errors.label = '표시명은 필수입니다';
    if (!formData.regularPrice || formData.regularPrice < 0) errors.regularPrice = '정상 가격은 필수입니다';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 시술 저장 (생성/수정)
  const handleSaveTreatment = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const requestData = {
        ...formData,
        regularPrice: parseInt(formData.regularPrice),
        eventPrice: formData.eventPrice ? parseInt(formData.eventPrice) : null,
        displayOrder: parseInt(formData.displayOrder),
      };

      if (editingTreatment) {
        // 수정
        await treatmentAPI.update(editingTreatment.id, requestData);
      } else {
        // 신규 등록
        await treatmentAPI.create(requestData);
      }

      await fetchTreatments();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving treatment:', err);
      setFormErrors({ general: err.response?.data?.message || '저장에 실패했습니다' });
    }
  };

  // 시술 삭제
  const handleDeleteTreatment = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      await treatmentAPI.delete(id);
      await fetchTreatments();
    } catch (err) {
      console.error('Error deleting treatment:', err);
      alert('삭제에 실패했습니다: ' + (err.response?.data?.message || err.message));
    }
  };

  // 이벤트 가격 토글
  const handleToggleEvent = async (id) => {
    try {
      await treatmentAPI.toggleEvent(id);
      await fetchTreatments();
    } catch (err) {
      console.error('Error toggling event price:', err);
      alert('이벤트 가격 토글에 실패했습니다');
    }
  };

  // 활성화 토글
  const handleToggleActive = async (id) => {
    try {
      await treatmentAPI.toggleActive(id);
      await fetchTreatments();
    } catch (err) {
      console.error('Error toggling active status:', err);
      alert('활성화 토글에 실패했습니다');
    }
  };

  if (loading) {
    return (
      <div className="treatment-management-container">
        <div className="loading">시술 목록을 불러오는 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="treatment-management-container">
        <div className="error-message">{error}</div>
        <Button onClick={fetchTreatments} variant="primary" size="medium">
          다시 시도
        </Button>
      </div>
    );
  }

  return (
    <div className="treatment-management-container">
      {/* 헤더 */}
      <header className="treatment-header">
        <div>
          <h1>시술 메뉴 관리</h1>
          <p className="subtitle">예약 페이지에 표시될 시술 목록을 관리합니다</p>
        </div>
        <Button onClick={handleOpenModal} variant="primary" size="medium">
          + 시술 추가
        </Button>
      </header>

      {/* 통계 */}
      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-label">전체 시술</div>
          <div className="stat-value">{treatments.length}개</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">활성화</div>
          <div className="stat-value">
            {treatments.filter(t => t.isActive).length}개
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">이벤트 진행 중</div>
          <div className="stat-value">
            {treatments.filter(t => t.useEventPrice).length}개
          </div>
        </div>
      </div>

      {/* 시술 목록 - 모바일 카드 뷰 */}
      <div className="treatment-cards">
        {treatments.length === 0 ? (
          <div className="no-data">등록된 시술이 없습니다.</div>
        ) : (
          treatments.map((treatment) => (
            <div
              key={treatment.id}
              className={`treatment-card ${!treatment.isActive ? 'inactive' : ''}`}
            >
              {/* 카드 헤더 */}
              <div className="treatment-card-header">
                <div className="treatment-card-title">
                  <h3 className="treatment-card-name">{treatment.name}</h3>
                  <p className="treatment-card-label">{treatment.label}</p>
                </div>
                <div className="treatment-card-order">#{treatment.displayOrder + 1}</div>
              </div>

              {/* 카드 바디 */}
              <div className="treatment-card-body">
                {treatment.category && (
                  <div className="treatment-card-field">
                    <span className="treatment-card-field-label">카테고리</span>
                    <span className={`category-badge ${treatment.category}`}>
                      {treatment.category}
                    </span>
                  </div>
                )}

                <div className="treatment-card-field">
                  <span className="treatment-card-field-label">정상가</span>
                  <span className="treatment-card-field-value">
                    {formatCurrency(treatment.regularPrice)}
                  </span>
                </div>

                {treatment.eventPrice && (
                  <div className="treatment-card-field">
                    <span className="treatment-card-field-label">이벤트가</span>
                    <span className="treatment-card-field-value event-price">
                      {formatCurrency(treatment.eventPrice)}
                    </span>
                  </div>
                )}

                <div className="treatment-card-field">
                  <span className="treatment-card-field-label">현재 가격</span>
                  <span className="treatment-card-field-value price">
                    {formatCurrency(treatment.currentPrice)}
                    {treatment.useEventPrice && <span className="event-badge"> 🎉</span>}
                  </span>
                </div>

                {treatment.duration && (
                  <div className="treatment-card-field">
                    <span className="treatment-card-field-label">소요시간</span>
                    <span className="treatment-card-field-value">{treatment.duration}</span>
                  </div>
                )}

                <div className="treatment-card-field">
                  <span className="treatment-card-field-label">상태</span>
                  <button
                    className={`status-toggle ${treatment.isActive ? 'active' : 'inactive'}`}
                    onClick={() => handleToggleActive(treatment.id)}
                  >
                    {treatment.isActive ? '활성' : '비활성'}
                  </button>
                </div>
              </div>

              {/* 카드 푸터 (액션 버튼) */}
              <div className="treatment-card-footer">
                <button
                  className="btn-event-toggle"
                  onClick={() => handleToggleEvent(treatment.id)}
                  disabled={!treatment.eventPrice}
                >
                  {treatment.useEventPrice ? '이벤트 OFF' : '이벤트 ON'}
                </button>
                <button
                  className="btn-edit"
                  onClick={() => handleEditTreatment(treatment)}
                >
                  수정
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDeleteTreatment(treatment.id)}
                >
                  삭제
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* 시술 목록 테이블 (PC용) */}
      <div className="table-container">
        <table className="treatment-table">
          <thead>
            <tr>
              <th className="col-order">No.</th>
              <th className="col-category">카테고리</th>
              <th className="col-name">시술명</th>
              <th className="col-label">표시명</th>
              <th className="col-price">정상 가격</th>
              <th className="col-price">이벤트 가격</th>
              <th className="col-price">현재 적용가</th>
              <th className="col-duration">소요시간</th>
              <th className="col-status">상태</th>
              <th className="col-actions">관리</th>
            </tr>
          </thead>
          <tbody>
            {treatments.length === 0 ? (
              <tr>
                <td colSpan="10" className="no-data">
                  등록된 시술이 없습니다.
                </td>
              </tr>
            ) : (
              treatments.map((treatment) => (
                <tr key={treatment.id} className={!treatment.isActive ? 'inactive-row' : ''}>
                  <td className="col-order">{treatment.displayOrder + 1}</td>
                  <td className="col-category">
                    {treatment.category ? (
                      <span className={`category-badge ${treatment.category}`}>
                        {treatment.category}
                      </span>
                    ) : (
                      <span className="category-badge-empty">-</span>
                    )}
                  </td>
                  <td className="col-name">
                    <strong className="treatment-name">{treatment.name}</strong>
                  </td>
                  <td className="col-label">
                    <span className="treatment-label">{treatment.label}</span>
                  </td>
                  <td className="col-price price-cell">
                    {formatCurrency(treatment.regularPrice)}
                  </td>
                  <td className="col-price price-cell event">
                    {treatment.eventPrice ? formatCurrency(treatment.eventPrice) : '-'}
                  </td>
                  <td className="col-price price-cell current">
                    <div className="current-price-wrapper">
                      <strong>{formatCurrency(treatment.currentPrice)}</strong>
                      {treatment.useEventPrice && (
                        <span className="event-badge" title="이벤트 가격 적용 중">🎉</span>
                      )}
                    </div>
                  </td>
                  <td className="col-duration">{treatment.duration || '-'}</td>
                  <td className="col-status">
                    <button
                      className={`status-toggle ${treatment.isActive ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleActive(treatment.id)}
                      title="클릭하여 활성화/비활성화 전환"
                    >
                      {treatment.isActive ? '활성' : '비활성'}
                    </button>
                  </td>
                  <td className="col-actions">
                    <div className="action-buttons">
                      <button
                        className="btn-event-toggle"
                        onClick={() => handleToggleEvent(treatment.id)}
                        disabled={!treatment.eventPrice}
                        title={treatment.eventPrice ? (treatment.useEventPrice ? '이벤트 가격 비활성화' : '이벤트 가격 활성화') : '이벤트 가격 미설정'}
                      >
                        {treatment.useEventPrice ? '이벤트 OFF' : '이벤트 ON'}
                      </button>
                      <button
                        className="btn-edit"
                        onClick={() => handleEditTreatment(treatment)}
                        title="시술 정보 수정"
                      >
                        수정
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteTreatment(treatment.id)}
                        title="시술 삭제"
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 모달 (생성/수정) */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTreatment ? '시술 수정' : '시술 추가'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>

            <form onSubmit={handleSaveTreatment} className="treatment-form">
              {formErrors.general && (
                <div className="form-error-message">{formErrors.general}</div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>시술명 *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="예: 윤곽관리"
                    className={formErrors.name ? 'error' : ''}
                  />
                  {formErrors.name && <span className="error-text">{formErrors.name}</span>}
                </div>

                <div className="form-group">
                  <label>카테고리</label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    placeholder="예: 시그니처, 페이셜, 바디"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>표시명 *</label>
                <input
                  type="text"
                  name="label"
                  value={formData.label}
                  onChange={handleChange}
                  placeholder="예: (시그니처) 윤곽관리 1회"
                  className={formErrors.label ? 'error' : ''}
                />
                {formErrors.label && <span className="error-text">{formErrors.label}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>정상 가격 * (원)</label>
                  <input
                    type="number"
                    name="regularPrice"
                    value={formData.regularPrice}
                    onChange={handleChange}
                    placeholder="250000"
                    min="0"
                    className={formErrors.regularPrice ? 'error' : ''}
                  />
                  {formErrors.regularPrice && <span className="error-text">{formErrors.regularPrice}</span>}
                </div>

                <div className="form-group">
                  <label>이벤트 가격 (원)</label>
                  <input
                    type="number"
                    name="eventPrice"
                    value={formData.eventPrice}
                    onChange={handleChange}
                    placeholder="200000"
                    min="0"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>소요시간</label>
                  <input
                    type="text"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    placeholder="예: 90분"
                  />
                </div>

                <div className="form-group">
                  <label>표시 순서</label>
                  <input
                    type="number"
                    name="displayOrder"
                    value={formData.displayOrder}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="useEventPrice"
                    checked={formData.useEventPrice}
                    onChange={handleChange}
                  />
                  이벤트 가격 사용
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                  />
                  활성화 (예약 페이지에 표시)
                </label>
              </div>

              <div className="modal-actions">
                <Button type="button" variant="outline" onClick={handleCloseModal}>
                  취소
                </Button>
                <Button type="submit" variant="primary">
                  {editingTreatment ? '수정' : '추가'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreatmentManagementPage;
